import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, FileText, Package, Scale, RefreshCcw, Shield as ShieldIcon,
  Mail, Globe, ExternalLink,
} from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import Footer from "@/components/Footer";

/* Legal + commercial pages required by the payment gateway (Tap Payments) and
   by general e-commerce transparency: /services, /products, /terms, /refund,
   /privacy. Each page is bilingual (ar/en) and shares one layout. */

const CONTACT_EMAIL = "info@pro-lancer.net";
const BUSINESS_NAME_AR = "مؤسسة براح الرقمية لتقنية المعلومات";
const BUSINESS_NAME_EN = "Barah Digital Information Technology Est.";
const CR_NUMBER = "7054088864";
const PRODUCT_URL = "https://prolancer.sa";

const PAGES = {
  services: {
    icon: FileText,
    titleAr: "الخدمات",
    titleEn: "Services",
    eyebrowAr: "ماذا نقدّم لك",
    eyebrowEn: "What we offer",
    descAr:
      "برولانسر منصّة رقمية تقدّم أدوات تسعير وتحليل لسوق العمل الحر في السعودية.",
    descEn:
      "Prolancer is a digital platform offering pricing and analytics tools for the Saudi freelance market.",
    blocksAr: [
      {
        h: "حاسبة التسعير الذكية",
        p: "أداة تولّد اقتراح سعر مخصص لخدمتك بناءً على نوعها، مستوى الخبرة، التعقيد، والنطاق — بدعم الذكاء الاصطناعي.",
      },
      {
        h: "حاسبة الدخل الشهري",
        p: "تحسب الدخل الشهري والسنوي المتوقّع للمستقل بناءً على سعر الساعة أو المشروع وعدد الطلبات وعمولة المنصّة.",
      },
      {
        h: "مرصد مؤشرات الأسعار (Market Observer)",
        p: "لوحة عامة تعرض متوسطات أسعار الخدمات في السوق السعودي خلال آخر 30 يومًا، للدراسات والتحليل والتسعير.",
      },
      {
        h: "اقتراحات ذكاء اصطناعي",
        p: "ثلاثة اقتراحات مجانية شهريًا، مع إمكانية الاشتراك للحصول على اقتراحات أكثر.",
      },
      {
        h: "محتوى تعليمي",
        p: "مدوّنة متخصّصة، أدلّة أسعار، وقصص نجاح لمساعدة المستقلين على فهم السوق وتطوير أعمالهم.",
      },
    ],
    blocksEn: [
      {
        h: "Smart pricing calculator",
        p: "Generates a tailored price suggestion for your service based on type, experience, complexity and scope — AI-assisted.",
      },
      {
        h: "Monthly earnings calculator",
        p: "Projects a freelancer's monthly and yearly earnings based on hourly or project rate, number of orders, and platform commission.",
      },
      {
        h: "Market Observer",
        p: "Public dashboard showing 30-day Saudi market price averages, for research, analysis, and pricing benchmarking.",
      },
      {
        h: "AI suggestions",
        p: "Three free AI suggestions per month, with optional subscription for additional usage.",
      },
      {
        h: "Educational content",
        p: "Specialized blog, pricing guides, and success stories to help freelancers understand the market and grow their business.",
      },
    ],
  },

  products: {
    icon: Package,
    titleAr: "المنتجات والاشتراكات",
    titleEn: "Products & subscriptions",
    eyebrowAr: "خطط الاستخدام",
    eyebrowEn: "Usage plans",
    descAr:
      "جميع الأسعار بالريال السعودي، شاملة ضريبة القيمة المضافة 15% حيث تنطبق، وتُحصّل عبر بوابة الدفع الإلكترونية (Tap Payments).",
    descEn:
      "All prices are in Saudi Riyals and include 15% VAT where applicable. Payments are collected through our electronic payment gateway (Tap Payments).",
    blocksAr: [
      {
        h: "الباقة المجانية",
        p: "ثلاثة اقتراحات تسعير مجانية شهريًا، وصول كامل لحاسبة التسعير وحاسبة الدخل والمرصد. مناسبة لتجربة المنصّة.",
      },
      {
        h: "اشتراك شهري غير محدود",
        p: "اقتراحات تسعير بالذكاء الاصطناعي بلا حد، حفظ مقترحات العملاء، تصدير العروض، ودعم أولوية. السعر وتفاصيل التجديد تُعرض عند الدفع.",
      },
      {
        h: "اشتراك سنوي",
        p: "جميع ميزات الاشتراك الشهري بسعر مخفّض يُدفع مرة واحدة سنويًا.",
      },
      {
        h: "API مفتوح (CC BY 4.0)",
        p: "واجهة برمجة مجانية بـ 100 طلب/يوم للاستخدامات البحثية والإعلامية، مع ترخيص CC BY 4.0 يُلزم بذكر المصدر.",
      },
      {
        h: "الدفع",
        p: "تُحصّل جميع المدفوعات عبر بوابة Tap Payments المرخّصة محليًا، التي تدعم مدى، فيزا، ماستركارد، وأبل باي. لا نحتفظ ببيانات البطاقات مباشرة.",
      },
    ],
    blocksEn: [
      {
        h: "Free plan",
        p: "Three free pricing suggestions per month, full access to the pricing & earnings calculators and the Market Observer. Great for trying the platform.",
      },
      {
        h: "Monthly unlimited subscription",
        p: "Unlimited AI pricing suggestions, client proposal saving, quote exports, and priority support. Price and renewal details are shown at checkout.",
      },
      {
        h: "Yearly subscription",
        p: "All monthly features at a discounted price, paid once per year.",
      },
      {
        h: "Open API (CC BY 4.0)",
        p: "Free developer API with 100 req/day for research and media use, under CC BY 4.0 license requiring source attribution.",
      },
      {
        h: "Payments",
        p: "All payments are processed via the locally-licensed Tap Payments gateway, supporting Mada, Visa, Mastercard and Apple Pay. We do not store card data directly.",
      },
    ],
  },

  terms: {
    icon: Scale,
    titleAr: "سياسة الاستخدام",
    titleEn: "Terms of use",
    eyebrowAr: "الشروط والأحكام",
    eyebrowEn: "Terms & conditions",
    descAr:
      "باستخدامك موقع برولانسر فإنك توافق على الشروط التالية. يُحتفظ بالحق في تحديث هذه الشروط دون إشعار مسبق، ويسري النص الأحدث فور نشره.",
    descEn:
      "By using the Prolancer website you agree to the following terms. We reserve the right to update these terms without prior notice; the latest version applies upon publication.",
    blocksAr: [
      {
        h: "1. طبيعة الخدمة",
        p: "برولانسر منصّة لعرض مؤشرات تسعير واقتراحات ذكاء اصطناعي للعمل الحر. الاقتراحات لأغراض استرشادية فقط ولا تمثّل تسعيرة إلزامية أو توصية قانونية أو مالية.",
      },
      {
        h: "2. الحساب والاستخدام",
        p: "يجب أن تقدّم بيانات صحيحة عند التسجيل، وتتحمّل مسؤولية حماية بيانات دخولك. يُمنع استخدام المنصّة لأي نشاط غير مشروع أو انتهاك لحقوق الآخرين.",
      },
      {
        h: "3. الملكية الفكرية",
        p: "جميع المحتويات والعلامات التجارية والأدوات والخوارزميات ملك خالص لمؤسسة براح الرقمية لتقنية المعلومات (brah-tech.com)، ولا يجوز نسخها أو إعادة توزيعها دون إذن خطي، باستثناء بيانات API المرخّصة تحت CC BY 4.0.",
      },
      {
        h: "4. المدفوعات والفوترة",
        p: "تُحصَّل جميع المدفوعات عبر بوابة الدفع الإلكترونية (Tap Payments). تُصدر فاتورة ضريبية إلكترونية متوافقة مع هيئة الزكاة والضريبة والجمارك (ZATCA) لكل عملية دفع ناجحة.",
      },
      {
        h: "5. حدود المسؤولية",
        p: "لا تتحمّل برولانسر أي مسؤولية عن قرارات العميل التجارية أو خسائر مباشرة أو غير مباشرة ناتجة عن استخدام الاقتراحات أو البيانات المنشورة.",
      },
      {
        h: "6. القانون المطبّق",
        p: "تخضع هذه الشروط لأنظمة المملكة العربية السعودية، وتُحلّ النزاعات أمام الجهة القضائية المختصّة في المملكة.",
      },
    ],
    blocksEn: [
      {
        h: "1. Nature of service",
        p: "Prolancer is a platform providing pricing indicators and AI suggestions for freelance work. All suggestions are indicative only and do not constitute a binding rate or legal/financial advice.",
      },
      {
        h: "2. Account & usage",
        p: "You must provide accurate information when registering and are responsible for safeguarding your credentials. Use for any illegal activity or infringement of others' rights is prohibited.",
      },
      {
        h: "3. Intellectual property",
        p: "All content, trademarks, tools, and algorithms are the exclusive property of Barah Digital Information Technology Est. (brah-tech.com) and may not be copied or redistributed without written permission, except API data licensed under CC BY 4.0.",
      },
      {
        h: "4. Payments & invoicing",
        p: "All payments are collected via the electronic payment gateway (Tap Payments). A ZATCA-compliant electronic tax invoice is issued for every successful payment.",
      },
      {
        h: "5. Limitation of liability",
        p: "Prolancer bears no responsibility for the user's commercial decisions or any direct or indirect losses arising from the use of its suggestions or published data.",
      },
      {
        h: "6. Governing law",
        p: "These terms are governed by the laws of the Kingdom of Saudi Arabia; disputes are resolved before the competent judicial authority in the Kingdom.",
      },
    ],
  },

  refund: {
    icon: RefreshCcw,
    titleAr: "سياسة الاسترداد",
    titleEn: "Refund policy",
    eyebrowAr: "الاسترداد والإلغاء",
    eyebrowEn: "Refunds & cancellations",
    descAr:
      "نلتزم بسياسة واضحة للاسترداد تتوافق مع نظام التجارة الإلكترونية السعودي ومع متطلبات بوابة الدفع (Tap Payments). يرجى قراءة البنود بعناية.",
    descEn:
      "We follow a clear refund policy aligned with the Saudi e-commerce law and the requirements of our payment gateway (Tap Payments). Please read the terms carefully.",
    blocksAr: [
      {
        h: "1. مدّة طلب الاسترداد",
        p: "يحق للمشترك طلب استرداد مبلغ اشتراكه خلال 7 أيام من تاريخ عملية الدفع، شرط عدم استخدام أكثر من 3 اقتراحات ذكاء اصطناعي خلال هذه المدّة.",
      },
      {
        h: "2. حالات عدم الاسترداد",
        p: "لا يُسترد المبلغ إذا تمّ استخدام أكثر من 3 اقتراحات، أو إذا انقضت فترة الـ 7 أيام، أو إذا كان الإلغاء بسبب مخالفة شروط الاستخدام من قبل المشترك.",
      },
      {
        h: "3. آلية الاسترداد",
        p: "ترسل طلب الاسترداد إلى info@pro-lancer.net مع ذكر إيميل الاشتراك ورقم عملية الدفع. تتم المعالجة خلال 3 أيام عمل، ويرجَّع المبلغ لنفس وسيلة الدفع الأصلية خلال 7 إلى 14 يوم عمل حسب البنك المُصدر.",
      },
      {
        h: "4. إلغاء التجديد التلقائي",
        p: "يمكنك إلغاء التجديد التلقائي للاشتراك في أي وقت من صفحة حسابك دون أي رسوم إضافية، ويبقى الاشتراك ساري المفعول حتى نهاية الفترة المدفوعة.",
      },
      {
        h: "5. المبالغ غير القابلة للاسترداد",
        p: "خدمات مخصّصة مقدّمة بناءً على طلب مشترك (مثل تقارير أو تطويرات حصرية) غير قابلة للاسترداد بعد البدء بتنفيذها.",
      },
      {
        h: "6. المدفوعات المتنازع عليها",
        p: "تخضع عمليات الدفع المتنازع عليها (chargebacks) لسياسة بوابة الدفع Tap Payments ونتعاون معها لتقديم الوثائق اللازمة.",
      },
    ],
    blocksEn: [
      {
        h: "1. Refund request window",
        p: "A subscriber may request a refund within 7 days of the payment date, provided no more than 3 AI suggestions have been used during this period.",
      },
      {
        h: "2. Non-refundable cases",
        p: "Refunds are not granted if more than 3 suggestions have been used, if the 7-day window has elapsed, or if the cancellation is due to a breach of the Terms of Use by the subscriber.",
      },
      {
        h: "3. Refund procedure",
        p: "Send your refund request to info@pro-lancer.net including your subscription email and payment reference. Processing takes up to 3 business days, and the amount is returned to the original payment method within 7-14 business days, depending on the issuing bank.",
      },
      {
        h: "4. Auto-renewal cancellation",
        p: "You can cancel auto-renewal at any time from your account page at no additional cost; the subscription remains active until the end of the paid period.",
      },
      {
        h: "5. Non-refundable items",
        p: "Custom services delivered on subscriber request (e.g. exclusive reports or developments) are non-refundable once work has started.",
      },
      {
        h: "6. Disputed payments",
        p: "Disputed payments (chargebacks) are subject to the Tap Payments gateway policy; we cooperate with them by providing the necessary documentation.",
      },
    ],
  },

  privacy: {
    icon: ShieldIcon,
    titleAr: "سياسة الخصوصية",
    titleEn: "Privacy policy",
    eyebrowAr: "حماية بياناتك",
    eyebrowEn: "Your data protection",
    descAr:
      "تحمي برولانسر بياناتك الشخصية وفقًا لنظام حماية البيانات الشخصية السعودي الصادر عن الهيئة السعودية للبيانات والذكاء الاصطناعي (SDAIA)، ومتطلبات بوابة الدفع Tap Payments.",
    descEn:
      "Prolancer protects your personal data in accordance with the Saudi Personal Data Protection Law issued by SDAIA and the requirements of the Tap Payments gateway.",
    blocksAr: [
      {
        h: "1. البيانات التي نجمعها",
        p: "الاسم، البريد الإلكتروني، معلومات الحساب، تاريخ الاستعلامات على المنصّة، معلومات تقنية (IP، نوع المتصفح، جلسة الاستخدام). لا نجمع بيانات بطاقتك البنكية مباشرة.",
      },
      {
        h: "2. الغرض من المعالجة",
        p: "تحسين الخدمة، تخصيص الاقتراحات، إرسال إشعارات الحساب والفواتير، التحليلات الكلية (مجهّلة الهوية) لبناء مؤشرات المرصد.",
      },
      {
        h: "3. مشاركة البيانات",
        p: "لا تُشارك بياناتك مع أي طرف ثالث إلا لأغراض محدّدة: بوابة Tap Payments لتنفيذ الدفع، ومزوّد الذكاء الاصطناعي (Anthropic) لمعالجة الاقتراحات، وجهات تنظيمية رسمية عند طلب قانوني.",
      },
      {
        h: "4. حقوقك",
        p: "يحق لك طلب الوصول لبياناتك، تصحيحها، حذفها، أو تحديد معالجتها، بإرسال طلب إلى info@pro-lancer.net. نلتزم بالرد خلال 10 أيام عمل.",
      },
      {
        h: "5. ملفات تعريف الارتباط (Cookies)",
        p: "نستخدم كوكيز أساسية لحفظ الجلسة واللغة، وكوكيز تحليلية لقياس الأداء. يمكنك تعطيلها من إعدادات متصفحك، لكن قد يتأثر بعض وظائف الموقع.",
      },
      {
        h: "6. فترة الاحتفاظ",
        p: "تُحتفظ ببيانات الحساب طوال فترة نشاط المستخدم، وتُحذف بعد 24 شهرًا من آخر نشاط إلا إذا طلبت الحذف الفوري.",
      },
      {
        h: "7. أمن المعلومات",
        p: "جميع الاتصالات مشفّرة بـ HTTPS/TLS، وكلمات المرور مجزّأة (hashed) بـ bcrypt. لا نحتفظ ببيانات البطاقات أبدًا على خوادمنا.",
      },
    ],
    blocksEn: [
      {
        h: "1. Data we collect",
        p: "Name, email, account information, platform query history, and technical info (IP, browser, session). We do not collect card data directly.",
      },
      {
        h: "2. Purpose of processing",
        p: "Service improvement, suggestion personalization, account and billing notifications, anonymized aggregate analytics for the Market Observer indicators.",
      },
      {
        h: "3. Data sharing",
        p: "Data is not shared with third parties except for defined purposes: Tap Payments for payment processing, our AI provider (Anthropic) for suggestion processing, and regulatory bodies upon lawful request.",
      },
      {
        h: "4. Your rights",
        p: "You have the right to access, correct, delete, or restrict processing of your data by sending a request to info@pro-lancer.net. We commit to respond within 10 business days.",
      },
      {
        h: "5. Cookies",
        p: "We use essential cookies for session and language, and analytics cookies to measure performance. You can disable them from your browser settings, though some site functions may be affected.",
      },
      {
        h: "6. Retention period",
        p: "Account data is retained for the user's active period and deleted 24 months after the last activity, unless immediate deletion is requested.",
      },
      {
        h: "7. Information security",
        p: "All communications are encrypted via HTTPS/TLS; passwords are bcrypt-hashed. We never store card data on our servers.",
      },
    ],
  },
};

export default function LegalPage({ page, lang: initialLang }) {
  const lang = initialLang || localStorage.getItem("pl_lang") || "ar";
  const ar = lang === "ar";
  const meta = PAGES[page];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.setAttribute("dir", ar ? "rtl" : "ltr");
    if (meta) {
      document.title = ar
        ? `${meta.titleAr} — برولانسر`
        : `${meta.titleEn} — Prolancer`;
    }
  }, [page, ar, meta]);

  if (!meta) return null;
  const Icon = meta.icon;
  const title = ar ? meta.titleAr : meta.titleEn;
  const eyebrow = ar ? meta.eyebrowAr : meta.eyebrowEn;
  const desc = ar ? meta.descAr : meta.descEn;
  const blocks = ar ? meta.blocksAr : meta.blocksEn;

  return (
    <div
      data-testid={`legal-page-${page}`}
      dir={ar ? "rtl" : "ltr"}
      className="min-h-screen bg-[#182138] text-white"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Thin sticky header */}
      <header
        className="sticky top-0 z-30 border-b border-[#2e3a5c] bg-[#182138]/85 backdrop-blur"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          <Link
            to="/"
            data-testid={`${page}-back-home`}
            className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 border border-[#2e3a5c] text-white hover:border-[#18c98f] hover:text-[#18c98f] transition font-arabic"
          >
            <ArrowLeft className={`h-3.5 w-3.5 ${ar ? "" : "rotate-180"}`} />
            {ar ? "العودة للحاسبة" : "Back to calculator"}
          </Link>
          <LogoLockup lang={lang} theme="dark" size="sm" />
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-extrabold text-[#18c98f]">
          <Icon className="h-3.5 w-3.5" />
          {eyebrow}
        </div>
        <h1 className={`mt-3 text-3xl sm:text-4xl lg:text-5xl font-black leading-tight ${ar ? "font-arabic" : "font-outfit"}`}>
          {title}
        </h1>
        <p className={`mt-4 max-w-3xl text-sm sm:text-base leading-relaxed text-white/75 ${ar ? "font-arabic" : "font-outfit"}`}>
          {desc}
        </p>
        <div className="mt-4 text-[11px] text-white/50">
          {ar ? "آخر تحديث: 5 أكتوبر 2026" : "Last updated: 5 October 2026"}
        </div>
      </section>

      {/* Content blocks */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-3" data-testid={`${page}-blocks`}>
          {blocks.map((b, i) => (
            <article
              key={i}
              className="rounded-2xl border border-[#2e3a5c] bg-[#1f2a47] p-5"
            >
              <h2 className={`text-[15px] sm:text-base font-black mb-2 text-[#18c98f] ${ar ? "font-arabic" : "font-outfit"}`}>
                {b.h}
              </h2>
              <p className={`text-[13px] sm:text-sm leading-relaxed text-white/80 ${ar ? "font-arabic" : "font-outfit"}`}>
                {b.p}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Contact + merchant info card */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="rounded-2xl border border-[#18c98f]/30 bg-gradient-to-br from-[#1f2a47] to-[#182138] p-6">
          <div className={`text-[13px] leading-relaxed text-white/80 ${ar ? "font-arabic" : "font-outfit"}`}>
            {ar ? (
              <>
                للاستفسار أو طلب تطبيق أي من البنود السابقة، تواصل معنا على{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#18c98f] font-bold">
                  {CONTACT_EMAIL}
                </a>
                . يُدير المنصّة <span className="font-bold">{BUSINESS_NAME_AR}</span>{" "}<a href="https://brah-tech.com" target="_blank" rel="noopener noreferrer" data-testid="legal-brah-link-ar" className="font-bold text-[#18c98f] hover:underline" dir="ltr">(brah-tech.com)</a>،
                سجل تجاري رقم{" "}
                <span className="font-mono-num font-bold" dir="ltr">
                  {CR_NUMBER}
                </span>.
              </>
            ) : (
              <>
                For inquiries or to apply any of the above clauses, contact us at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#18c98f] font-bold">
                  {CONTACT_EMAIL}
                </a>
                . The platform is operated by{" "}
                <span className="font-bold">{BUSINESS_NAME_EN}</span>{" "}<a href="https://brah-tech.com" target="_blank" rel="noopener noreferrer" data-testid="legal-brah-link-en" className="font-bold text-[#18c98f] hover:underline" dir="ltr">(brah-tech.com)</a>, Commercial
                Registration No.{" "}
                <span className="font-mono-num font-bold">{CR_NUMBER}</span>.
              </>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#18c98f] text-white text-xs font-black hover:bg-[#18c98f] transition"
            >
              <Mail className="h-3.5 w-3.5" />
              {ar ? "راسلنا" : "Email us"}
            </a>
            <a
              href={PRODUCT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#18c98f] text-[#18c98f] text-xs font-black hover:bg-[#18c98f]/10 transition"
            >
              <Globe className="h-3.5 w-3.5" />
              prolancer.sa
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </section>

      <Footer lang={lang} theme="dark" />
    </div>
  );
}
